import { CommonModule } from '@angular/common';
import { Component, computed, OnInit, signal, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { RequestBuilderService } from '../../../../shared/services/request-builder.service';
import { TooltipOptions } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MultiSelect, MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule } from 'primeng/table';
import { TagComponent } from '../../../../shared/components/tag/tag.component';
import { RideStatusType } from '../../../../shared/enums/rideStatusType';
import { StateService } from '../../../../shared/services/state.service';
import { MapRideServiceService } from '../../services/map-ride-service.service';

interface StudentRide {
  rideCargoId: number;
  rideId: number;
  studentName: string;
  rideTime: string;
  rideStatus: string;
  ridePeriod: string;
  rideType: string;
  jobStatusId: number;
  notifiedApplied: number;
  rideStatusId: number;
  scheduledFirstArrivalTimeutc?: string;
}

@Component({
  selector: 'app-cancel-student-ride-dialog',
  standalone: true,
  imports: [ButtonComponent, DialogModule, MultiSelectModule, TooltipModule, ReactiveFormsModule, CommonModule, SelectModule, TableModule, TagComponent],
  templateUrl: './cancel-student-ride-dialog.component.html',
  styleUrl: './cancel-student-ride-dialog.component.scss',
})
export class CancelStudentRideDialogComponent implements OnInit {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly fb = inject(FormBuilder);
  private readonly stateService = inject(StateService);
  private readonly mapRideService = inject(MapRideServiceService);

  students = signal<{ label: string; value: number }[]>([]);
  periods = signal<{ label: string; value: number }[]>([]);
  rideTypes = signal<{ label: string; value: number }[]>([]);
  studentRides = signal<StudentRide[]>([]);
  selectedStudentRides: StudentRide[] = [];
  loading = signal<boolean>(false);
  searched = signal<boolean>(false);

  // Track form values as signals
  selectedStudents = signal<any[]>([]);

  // Computed student lookup map for O(1) access
  studentsMap = computed(() => {
    const map = new Map<number, string>();
    this.students().forEach(student => {
      map.set(student.value, student.label);
    });
    return map;
  });

  // Computed tooltips with O(n) complexity
  studentsTooltip = computed(() => {
    const selectedValues = this.selectedStudents();
    if (!selectedValues || selectedValues.length === 0) {
      return 'Students Names';
    }

    const studentsLookup = this.studentsMap();
    const studentLabels = selectedValues
      .map(value => studentsLookup.get(value))
      .filter(Boolean) // Remove undefined labels
      .join(', ');

    return studentLabels || 'Students Names';
  });

  form!: FormGroup;
  rows = 10;
  first = 0;
  totalRecords = 0;
  rowsPerPageOptions: number[] = [5, 10, 25, 50, 100];
  columns: any[] = [];

  tooltipOptions: TooltipOptions = {
    showDelay: 500,
    tooltipEvent: 'hover',
    tooltipPosition: 'bottom',
  };
  confirmationDialogVisible = false;

  ngOnInit() {
    this.students.set(this.config.data.students ?? []);
    this.periods.set(this.config.data.periods ?? []);
    this.periods.set(this.periods().map(p => (p.value === 3 ? { ...p, value: 0 } : p)));
    this.rideTypes.set([
      { label: 'All Types', value: 0 },
      { label: 'Return', value: 1 },
      { label: 'Outbound', value: 2 },
    ]);

    this.columns = [
      { field: 'rideId', name: 'Ride Id', type: 'text' },
      { field: 'studentName', name: 'Student Name', type: 'text' },
      { field: 'rideTime', name: 'Time (Start - End)', type: 'text' },
      { field: 'rideStatus', name: 'Status', type: 'text' },
      { field: 'ridePeriod', name: 'Period', type: 'text' },
      { field: 'rideType', name: 'Ride Type', type: 'text' },
    ];

    this.initForm();
  }

  openConfirmDialog() {
    this.confirmationDialogVisible = true;
  }

  closeConfirmDialog() {
    this.confirmationDialogVisible = false;
  }

  confirmAction() {
    const clientName = JSON.parse(localStorage.getItem('currentUser') ?? '{}')?.identity?.client_name ?? 'Client';
    const body: any = {
      rideCargoIds: this.selectedStudentRides.map(ride => ride.rideCargoId),
      cancellationStatusId: RideStatusType.Automatic,
      cancellationReason: this.stateService.getCancellationReasonsLookup().find(r => r.name === 'Other')?.id,
      internalNote: `Cancelled by ${clientName}`,
      notifyGuardians: true,
      isDispatchMassStudentCancellation: true,
    };

    this.requestBuilder.post(`rides/mass-cancellation-operation`, body).subscribe({
      next: () => {
        this.loading.set(false);
        this.ref.close({ confirmed: true });
      },
      error: error => {
        this.loading.set(false);
      },
    });
  }

  clearFormControl(controlName: string) {
    this.form.get(controlName)?.setValue(null);
  }

  initForm() {
    this.form = this.fb.group({
      students: [null, Validators.required],
      period: [0, Validators.required],
      rideType: [0, Validators.required],
    });

    // Track form value changes and update signals
    this.form.get('students')?.valueChanges.subscribe(value => {
      this.selectedStudents.set(value ?? []);
    });
  }

  onPage(event: any) {
    this.first = event.first;
    this.rows = event.rows;

    if (this.form.valid) {
      this.search();
    }
  }

  search(resetFirst: boolean = false) {
    if (resetFirst) this.first = 0;

    this.loading.set(true);
    this.searched.set(true);
    const body = {
      studentIds:
        this.form.value.students?.map((s: any) => {
          if (s && typeof s === 'object') return s.value;
          else return s;
        }) ?? [],
      ridePeriod: this.form.value.period,
      rideType: this.form.value.rideType,
      take: this.rows,
      skip: this.first,
    };

    this.requestBuilder.post(`rides/cancel-student-ride`, body).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        this.studentRides.set(
          res.data.data.map((ride: StudentRide) => {
            return {
              ...ride,
              statusSeverity: this.mapRideService.getRideAttentionFlag(ride.jobStatusId, this.config.data.currentDate, ride.scheduledFirstArrivalTimeutc, ride.rideStatusId, ride.notifiedApplied).hasAttention ? 'danger' : 'info',
            };
          }) ?? [],
        );
        this.totalRecords = res.data.total ?? 0;
      },
      error: (err: any) => {
        console.error(err);
        this.loading.set(false);
      },
    });
  }

  isSelected(rowData: StudentRide): boolean {
    return this.selectedStudentRides.some(selected => selected.rideCargoId === rowData.rideCargoId);
  }

  clearMultiSelectFilter(multiSelectRef: MultiSelect) {
    if (multiSelectRef?.filterValue) {
      multiSelectRef.filterValue = '';
    }
  }
}
